// registration-api is a platform-wide singleton (platform/global-services),
// fixed public address, same as kwms-web's own copy of this constant.
// Trimmed to just the QR web-login link ceremony -- unlike kwms-web,
// nodeowner-web has no badge/phone-form login path to also support here.
export const REGISTRATION_API_URL = "https://register.uskorin.tech";

export interface GlobalTokenSet {
  accessToken: string;
  idToken?: string;
  refreshToken?: string;
  tokenType: string;
  expiresAtEpochSeconds: number;
  userUid: string;
  phone: string;
}

export async function errorMessage(response: Response, fallback: string): Promise<string> {
  const body: { error?: string } | null = await response.json().catch(() => null);
  return body?.error ?? fallback;
}

// Same versioned-prefix convention as kwms-web's own WEB_LOGIN_QR_PREFIX --
// kept in sync with registration-api's link.rs manually, no shared schema
// between the repos.
const WEB_LOGIN_QR_PREFIX = "WLL.01.";

export interface WebLoginLink {
  linkId: string;
  qrContent: string;
}

/**
 * `POST /link/start` -- mints a short-lived link_id, no auth needed.
 * `qrContent` is what WebLoginQr.tsx encodes into the rendered QR image.
 */
export async function startWebLoginLink(): Promise<WebLoginLink> {
  // credentials: "include" -- registration-api runs 2+ replicas with
  // in-memory per-link state behind a sticky-session cookie, same
  // reasoning as kwms-web's own startWebLoginLink.
  const response = await fetch(`${REGISTRATION_API_URL}/link/start`, {
    method: "POST",
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Не удалось получить QR-код"));
  }
  const body: { link_id: string } = await response.json();
  return { linkId: body.link_id, qrContent: `${WEB_LOGIN_QR_PREFIX}${body.link_id}` };
}

export type WebLoginLinkStatus =
  | { status: "pending" }
  | { status: "not_found" }
  | { status: "approved"; tokens: GlobalTokenSet };

/**
 * `GET /link/{id}` -- polled by WebLoginQr.tsx while the code is on
 * screen. `not_found` covers both "never existed" and "expired/already
 * claimed" identically -- the poller just requests a fresh one either way.
 */
export async function pollWebLoginLink(linkId: string): Promise<WebLoginLinkStatus> {
  const response = await fetch(`${REGISTRATION_API_URL}/link/${linkId}`, { credentials: "include" });
  if (response.status === 404) return { status: "not_found" };
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Не удалось проверить QR-код"));
  }
  const body: {
    status: string;
    phone?: string;
    user_uid?: string;
    access_token?: string;
    id_token?: string;
    refresh_token?: string;
    expires_in?: number;
    token_type?: string;
  } = await response.json();

  if (body.status !== "approved") return { status: "pending" };
  return {
    status: "approved",
    tokens: {
      accessToken: body.access_token!,
      idToken: body.id_token,
      refreshToken: body.refresh_token,
      tokenType: body.token_type!,
      expiresAtEpochSeconds: Math.floor(Date.now() / 1000) + body.expires_in!,
      userUid: body.user_uid!,
      phone: body.phone!,
    },
  };
}
